/**
 * In-app browser (webview) detection.
 *
 * Links tapped inside Instagram / Facebook / Threads / TikTok / Messenger
 * open in the host app's embedded webview, not Safari or Chrome. Two
 * things break there that matter for us:
 *
 *   1. Google blocks OAuth in embedded webviews (`disallowed_useragent`),
 *      so `signInWithPopup` / `signInWithRedirect` both fail.
 *   2. Storage is partitioned per host app, so a room saved in the
 *      Instagram webview is invisible when the user later opens Safari.
 *
 * Everything that asks the user to sign in from a paid-social landing
 * should check this first and offer "open in your browser" instead of a
 * Google button that silently fails. UA sniffing is the only signal
 * available; the token list is the well-known set each host app injects.
 */

export type InAppBrowserHost =
  | "instagram"
  | "facebook"
  | "messenger"
  | "threads"
  | "tiktok"
  | "snapchat"
  | "linkedin"
  | "twitter"
  | "pinterest"
  | "other";

const HOST_TOKENS: ReadonlyArray<readonly [RegExp, InAppBrowserHost]> = [
  [/\bInstagram\b/i, "instagram"],
  [/\bBarcelona\b/i, "threads"], // Threads' internal codename in its UA
  [/\bMessengerForiOS\b|\bOrca-Android\b|\[FB_IAB\/MESSENGER/i, "messenger"],
  [/\bFBAN\b|\bFBAV\b|\bFB_IAB\b|\bFBIOS\b/i, "facebook"],
  [/BytedanceWebview|musical_ly|\bTikTok\b/i, "tiktok"],
  [/\bSnapchat\b/i, "snapchat"],
  [/\bLinkedInApp\b/i, "linkedin"],
  [/\bTwitter\b|\bTwitterAndroid\b/i, "twitter"],
  [/\bPinterest\b/i, "pinterest"],
];

/** Detect the hosting app from a user-agent string. `null` = real browser. */
export function detectInAppBrowser(userAgent: string): InAppBrowserHost | null {
  for (const [re, host] of HOST_TOKENS) {
    if (re.test(userAgent)) return host;
  }
  // iOS WKWebView without Safari's `Safari/` token is a generic webview
  // (many smaller apps). Real iOS Safari always advertises `Safari/`.
  if (/\b(iPhone|iPad|iPod)\b/.test(userAgent) && /\bAppleWebKit\b/.test(userAgent)) {
    if (!/\bSafari\//.test(userAgent) && !/\bCriOS\/|\bFxiOS\/|\bEdgiOS\//.test(userAgent)) {
      return "other";
    }
  }
  // Android WebView advertises `; wv)` in the platform token.
  if (/\bAndroid\b/.test(userAgent) && /;\s*wv\)/.test(userAgent)) return "other";
  return null;
}

let cached: InAppBrowserHost | null | undefined;

/** Which in-app browser we're running inside, or `null` for a real browser. */
export function inAppBrowserHost(): InAppBrowserHost | null {
  if (cached !== undefined) return cached;
  cached =
    typeof navigator === "undefined" ? null : detectInAppBrowser(navigator.userAgent || "");
  return cached;
}

export function isInAppBrowser(): boolean {
  return inAppBrowserHost() !== null;
}

/**
 * Best-effort handoff to the system browser. Android supports an
 * `intent://` URL that asks the OS to open the page in the default
 * browser (with a fallback back to the https URL). iOS has no universal
 * equivalent: Facebook and Messenger honour an `x-safari-https://` scheme
 * from a real tap; Instagram does not, so callers show a "link copied,
 * paste it in Safari" hint there. Returns true when a handoff was
 * attempted (not whether it succeeded — iOS can't tell).
 */
export function openInSystemBrowser(url: string, host: InAppBrowserHost | null = inAppBrowserHost()): boolean {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/\bAndroid\b/.test(ua)) {
    try {
      const u = new URL(url);
      const scheme = u.protocol.replace(":", "");
      const rest = url.replace(/^[a-z]+:\/\//i, "");
      window.location.href =
        `intent://${rest}#Intent;scheme=${scheme};action=android.intent.action.VIEW;` +
        `S.browser_fallback_url=${encodeURIComponent(url)};end`;
      return true;
    } catch {
      return false;
    }
  }
  if (/iPhone|iPad|iPod/.test(ua) && (host === "facebook" || host === "messenger")) {
    try {
      window.location.href = url.replace(/^https:\/\//i, "x-safari-https://");
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * Move the visitor to their real browser: Android (and Facebook on iOS)
 * open it directly; everything else gets the link on the clipboard to
 * paste into Safari. When an iOS scheme jump was attempted but the page is
 * still here after a moment, fall back to the clipboard too.
 */
export async function handOffToSystemBrowser(
  url: string
): Promise<"opened" | "copied" | "failed"> {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent || "";
  const attempted = openInSystemBrowser(url);
  if (attempted && /\bAndroid\b/.test(ua)) return "opened";
  if (attempted) {
    await new Promise((r) => setTimeout(r, 1200));
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return "opened";
  }
  try {
    await navigator.clipboard.writeText(url);
    return "copied";
  } catch {
    return "failed";
  }
}

/** Display name of each host app, for "sign-in doesn't work inside …" copy. */
export const IN_APP_HOST_LABEL: Record<InAppBrowserHost, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  messenger: "Messenger",
  threads: "Threads",
  tiktok: "TikTok",
  snapchat: "Snapchat",
  linkedin: "LinkedIn",
  twitter: "X",
  pinterest: "Pinterest",
  other: "this app",
};

/** "Safari" on iOS, "Chrome" on Android — for "Open in …" copy. */
export function systemBrowserName(): string {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent || "";
  return /\bAndroid\b/.test(ua) ? "Chrome" : "Safari";
}

/** Test-only. */
export function _resetInAppBrowserForTests(): void {
  cached = undefined;
}
